import type {
  Pin,
  PinFoldersResponse,
  SearchPinsResponse,
  PinsInFolderResponse,
  CreatePinPayload,
  EditPinResponse,
  EditPinPayload,
  PinCommentsResponse,
  CreatePinCommentPayload,
  PinComment,
  ListPinFoldersPayload_SortField,
  PinReferenceResponse,
  PinReference,
  HasPinsForResponse,
  UnreadPinsResponse,
} from '@zendesk/zqa-services/pins'

import { api } from '@/api'
import { uploadUnsavedImages } from '@/modules/shared/CommentEditor/plugins/useUnsavedImages'

export const getPinReferencePreview = ({ type, value }: PinReference) =>
  api
    .extend({ hooks: { afterResponse: undefined } })
    .extend({ hooks: { afterResponse: [] } })
    .get(`pins/reference/${type}/${value}`)
    .json<PinReferenceResponse>()

export const createPin = async (json: CreatePinPayload) => {
  const data = await api.post('pins', { json }).json<Pin>()
  await uploadUnsavedImages(json.body)
  return data
}

export const editPin = async (pinId: string, json: Omit<EditPinPayload, 'id'>) => {
  const data = await api.put(`pins/${pinId}`, { json }).json<EditPinResponse>()
  await uploadUnsavedImages(json.body)
  return data
}

export const listPinFolders = (sort: ListPinFoldersPayload_SortField) =>
  api.get('pins/folders', { searchParams: { sort } }).json<PinFoldersResponse>()

export const searchPins = (query: string) =>
  api.get('pins/search', { searchParams: { query } }).json<SearchPinsResponse>()

export const getPinsInFolder = (folderId: string) => api.get(`pins/folders/${folderId}`).json<PinsInFolderResponse>()

export const getPinsByUserFolder = (userId: string, params?: { fromDate?: string; toDate?: string }) => {
  const query = new URLSearchParams()
  if (params?.fromDate) query.append('fromDate', params.fromDate)
  if (params?.toDate) query.append('toDate', params.toDate)

  return api
    .extend({ hooks: { afterResponse: undefined } })
    .extend({ hooks: { afterResponse: [] } })
    .get(`pins/folders/by-user/${userId}?${query}`)
    .json<PinsInFolderResponse>()
}

export const deletePin = (pinId: string) => api.delete(`pins/${pinId}`).json()

export const getPinComments = (pinId: string) => api.get(`pins/${pinId}/comments`).json<PinCommentsResponse>()

export const createPinComment = async (pinId: string, json: Omit<CreatePinCommentPayload, 'pinId'>) => {
  const data = await api.post(`pins/${pinId}/comments`, { json }).json<PinComment>()
  await uploadUnsavedImages(json.body)
  return data
}

export const deletePinComment = (pinId: string, commentId: string) =>
  api.delete(`pins/${pinId}/comments/${commentId}`).json()

export const editPinComment = async (pinId: string, commentId: string, body: string) => {
  const data = await api.put(`pins/${pinId}/comments/${commentId}`, { json: { body } }).json()
  await uploadUnsavedImages(body)
  return data
}

export const addPinParticipant = (pinId: string, userId: string) =>
  api.post(`pins/${pinId}/participants/${userId}`).json()

export const removePinParticipant = (pinId: string, userId: string) =>
  api.delete(`pins/${pinId}/participants/${userId}`).json()

export const getUnreadPinsCount = () => api.get('pins/unread-count').json<UnreadPinsResponse>()

export const markPinsViewed = (ids: string[]) => api.post('pins/mark-viewed', { json: { ids } }).json()

export const hasPinsFor = ({ type, value }: PinReference) =>
  api.get(`pins/exists-for/${type}/${value}`).json<HasPinsForResponse>()
